import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Text } from '../../../Components'
import { CHART_TOOLTIP_STYLE } from '../../../Components/Charts/chartTheme'
import { ScatterChartComponent } from '../../../Components/Charts/ScatterChart'
import { useCQuery } from '../../../Services'
import { formatEuro3Decimals } from '../../../Services/Functions'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { getDate } from './pricingFunctions'

const language = cap.language.state || 'IT'
const t = translations[language] || translations.IT

const timeDataKeys = [{ dataKey: 'totalPrice', color: 'var(--primary)' }]

/**
 * Componente timeline dei costi per conversazione con grafico scatter.
 * Mostra i costi nel tempo con punti cliccabili per navigare ai dettagli.
 * @param {Object} props - Proprietà del componente
 * @param {Array} props.dates - Array con [startDate, endDate] nel formato DD/MM/YYYY
 * @returns {JSX.Element|null} Card con grafico scatter timeline
 */
export const PricingTimeline = ({ dates }) => {
  const { data: { conversations = [] } = {}, isSuccess } = useCQuery(['getStats', ...dates])
  const [totalPrices, setTotalPrices] = useState([])
  const [averagePrice, setAveragePrice] = useState(0)
  const navigate = useNavigate()

  useEffect(() => {
    if (isSuccess) {
      setTotalPrices([
        { createdAt: getDate(dates[0]).getTime(), conversationId: '' },
        ...conversations
          .filter(({ totalPrice }) => totalPrice)
          .map((conversation) => ({
            createdAt: new Date(conversation.createdAt).getTime(),
            totalPrice: conversation?.totalPrice || 0,
            conversationId: conversation.id
          })),
        { createdAt: getDate(dates[1]).getTime() + 86400000, conversationId: '' }
      ])
      setAveragePrice(
        conversations.reduce((acc, { totalPrice }) => acc + totalPrice, 0) / conversations.length
      )
    }
  }, [conversations])

  if (!isSuccess) return null

  return (
    <div className="stats-pricing-timeline">
      <div className="stats-pricing-timeline-header">
        <span className="stats-pricing-timeline-title">{t.stCostoConversazioniTempo}</span>
      </div>
      <div className="stats-pricing-timeline-avg">
        {t.stCostoMedioPeriodo} {formatEuro3Decimals(averagePrice)}
      </div>
      <div className="stats-pricing-timeline-chart">
        <ScatterChartComponent
          data={totalPrices}
          dataKeys={timeDataKeys}
          xaxis="createdAt"
          yaxis
          cartesian
          xAxisProps={{
            dataKey: 'createdAt',
            type: 'number',
            tickCount: 3,
            domain: ['dataMin', 'dataMax'],
            tickFormatter: (unixTime) => new Date(unixTime).toLocaleString(),
            interval: 'preserveStartEnd'
          }}
          yAxisProps={{
            type: 'number',
            dataKey: 'totalPrice',
            tickFormatter: (value) => formatEuro3Decimals(value)
          }}
          tooltipProps={{
            content: ({
              payload: [{ payload: { conversationId = '', totalPrice } = {} } = {}] = []
            }) => (
              <div style={CHART_TOOLTIP_STYLE}>
                <Text value={`Id: ${conversationId}`} size={12} />
                <Text value={`${t.stCosto}: ${formatEuro3Decimals(totalPrice)}`} size={13} bold />
              </div>
            )
          }}
          scatterProps={{
            onClick: ({ payload: { conversationId = '' } = {} }) =>
              navigate(`/pricing/${conversationId}`)
          }}
        />
      </div>
    </div>
  )
}
